// piano-frontend/src/pages/AdminLessonPage.js

import React, { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Chip, TextField, FormControlLabel, Checkbox, ButtonBase,
  FormControl, IconButton, Select, MenuItem, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert, Divider,
  CircularProgress, LinearProgress, useTheme
} from "@mui/material";
import { Settings, LockOpen, Delete, CalendarToday, Edit, Check, AutoAwesome } from "@mui/icons-material";
import api from "../api/axios";
import dayjs from "dayjs";
import LessonConfigDialog from "../components/LessonConfigDialog";
import DateImpactPreview from "../components/DateImpactPreview";
import MemberPicker from "../components/MemberPicker";
import DateField from "../components/DateField";
import WeekJumpPicker, { getCurrentWeekStart } from "../components/WeekJumpPicker";
import useWeekTransition from "../hooks/useWeekTransition";
import { useWeekParam } from "../hooks/useCalendarUrlState";
import useHorizontalSwipe from "../hooks/useHorizontalSwipe";
import useCalendarShortcuts from "../hooks/useCalendarShortcuts";
import { motionSx } from "../utils/calendarMotion";
import { useDialog } from "../contexts/DialogProvider";

const TIME_OPTIONS = [
  "10:00", "10:30", "11:00", "11:30","12:00", "12:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
  "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00", "20:30",
  "21:00", "21:30", "22:00"
];

// 세로로 긴 시간표에서 주간 이동/요일 확인을 위해 매번 최상단으로 스크롤하지 않도록,
// 주간 이동 바와 요일 헤더를 상단에 고정한다.
const TOP_HEADER_HEIGHT = 56;   // App.js의 고정 TopHeader 높이
const WEEK_NAV_HEIGHT = 48;     // 아래 주간 이동 바의 고정 높이
const TABLE_GRID_COLUMNS = "clamp(45px, 12vw, 70px) repeat(6, minmax(0, 1fr))";

function AdminLessonPage({ user, setUser, onSwitchVersion }) {
  const theme = useTheme();
  const { confirm, alert, toast } = useDialog();
  const isDark = theme.palette.mode === 'dark';
  const isAdminOrTeacher = user.role === "ADMIN" || user.role === "TEACHER";
  const isAdmin = user.role === "ADMIN";
  const isTeacher = user.role === "TEACHER";
  const MY_LESSON_BG = isDark ? "#1565c0" : "#1976d2";
  
  const weekDays = ["월","화","수","목","금","토"];
  const dayKo = { SUN: "일", MON: "월", TUE: "화", WED: "수", THU: "목", FRI: "금", SAT: "토" };
  const dayNames = ["SUN","MON","TUE","WED","THU","FRI","SAT"];

  function getOverrideForSlot(teacher, date) {
    return teacher.overrides?.find(o => o.date === date) || null;
  }

  function getEndTime(start) {
    if (!start) return "";
    let [h, m] = start.split(":").map(Number);
    m += 30;
    h += Math.floor(m/60);
    m = m % 60;
    if(h>23) h=23;
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
  }

  const [weekStart, setWeekStart] = useWeekParam(); // 보던 주는 URL(?week=)에 유지
  const [weekDates, setWeekDates] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [members, setMembers] = useState([]);
  const [dialog, setDialog] = useState({ open: false, reservationType: 'block-once' });
  const [unblockDialog, setUnblockDialog] = useState({ open: false });
  const [holidayDialog, setHolidayDialog] = useState({ open: false, date: null, refundTickets: true });
  const [restoreDialog, setRestoreDialog] = useState({ open: false, date: null, holiday: null });
  const [holidays, setHolidays] = useState([]);
  const [lessonConfig, setLessonConfig] = useState(null);
  const [lessonOverrides, setLessonOverrides] = useState([]);
  const [configDialog, setConfigDialog] = useState(false);
  const [blockSchedules, setBlockSchedules] = useState([]);
  
  // 변동 스케줄 관리 state
  const [teacherSelectDialog, setTeacherSelectDialog] = useState(false);
  const [overrideManageDialog, setOverrideManageDialog] = useState({ open: false, teacher: null });
  const [scheduleOverrides, setScheduleOverrides] = useState([]);
  const [overrideForm, setOverrideForm] = useState({
    date: "",
    type: "OFF",
    work_start: "09:00",
    work_end: "18:00",
    reason: ""
  });
  const [loading, setLoading] = useState(false);
  const [editingOverrideId, setEditingOverrideId] = useState(null);

  // 레슨 설정 불러오기
  useEffect(() => {
    api.get("/lesson-config")
      .then(res => setLessonConfig(res.data))
      .catch(() => setLessonConfig({
        weekday_start_time: "14:00",
        weekday_end_time: "22:00",
        weekend_start_time: "12:00",
        weekend_end_time: "17:00",
        slot_duration: 30
      }));
  }, []);

  // 예외 설정 불러오기
  useEffect(() => {
    api.get("/lesson-config/overrides")
      .then(res => setLessonOverrides(res.data));
  }, []);

  // 블록 스케줄 불러오기
  useEffect(() => {
    api.get("/block-schedules")
      .then(res => setBlockSchedules(res.data));
  }, []);

  // 요일별 기본 레슨 시간 (요일 컬럼 우선, 없으면 레거시 평일/주말 필드로 폴백)
  const DAY_KEYS_EN = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const getBaseWindowForDow = (dow) => {
    if (!lessonConfig) return null;
    const key = DAY_KEYS_EN[dow];
    const s = lessonConfig[`${key}_start_time`]?.slice(0, 5);
    const e = lessonConfig[`${key}_end_time`]?.slice(0, 5);
    if (s && e) return { start: s, end: e };
    if (dow === 0) return null; // 일요일: 요일별 설정이 없으면 휴무
    const isWeekend = dow === 6;
    const ls = isWeekend ? lessonConfig.weekend_start_time?.slice(0, 5) : lessonConfig.weekday_start_time?.slice(0, 5);
    const le = isWeekend ? lessonConfig.weekend_end_time?.slice(0, 5) : lessonConfig.weekday_end_time?.slice(0, 5);
    if (ls && le) return { start: ls, end: le };
    return null;
  };

  // 날짜별 유효 설정 확인 함수
  const getEffectiveConfigForDate = (dateStr) => {
    const override = lessonOverrides.find(o => o.date === dateStr);
    const base = getBaseWindowForDow(dayjs(dateStr).day());

    if (override) {
      if (!override.is_open) {
        return { is_open: false, is_override: true, reason: override.reason };
      }
      return {
        is_open: true,
        start_time: override.start_time?.slice(0, 5) || base?.start,
        end_time: override.end_time?.slice(0, 5) || base?.end,
        is_override: true,
        reason: override.reason
      };
    }

    if (!base) return { is_open: false, is_override: false };
    return { is_open: true, start_time: base.start, end_time: base.end, is_override: false };
  };

  // 시간 구간 → 슬롯 목록
  const slotsFromRange = (start, end) => {
    const [startH, startM] = start.split(":").map(Number);
    const [endH, endM] = end.split(":").map(Number);
    const duration = lessonConfig?.slot_duration || 30;
    const slots = [];
    for (let m = startH * 60 + startM; m < endH * 60 + endM; m += duration) {
      slots.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
    }
    return slots;
  };

  const getAllTimeSlots = () => {
    if (!lessonConfig) return [];

    const allSlots = [];
    for (let dow = 1; dow <= 6; dow++) {
      const base = getBaseWindowForDow(dow);
      if (base) allSlots.push(...slotsFromRange(base.start, base.end));
    }
    lessonOverrides.forEach(o => {
      if (o.is_open && o.start_time && o.end_time) {
        allSlots.push(...slotsFromRange(o.start_time.slice(0, 5), o.end_time.slice(0, 5)));
      }
    });

    return [...new Set(allSlots)].sort();
  };

  const isTimeSlotValidForDate = (dateStr, time) => {
    const effectiveConfig = getEffectiveConfigForDate(dateStr);
    
    if (!effectiveConfig.is_open) return false;
    
    const startTime = effectiveConfig.start_time;
    const endTime = effectiveConfig.end_time;
    
    if (!startTime || !endTime) return false;
    
    return time >= startTime && time < endTime;
  };

  // 블록 스케줄 확인 함수 (예외 포함)
  const getBlockScheduleInfo = (teacherId, date, time) => {
    const dayOfWeek = dayNames[dayjs(date).day()];
    const blockSchedule = blockSchedules.find(bs => 
      bs.teacher_id === teacherId && 
      bs.day_of_week === dayOfWeek && 
      bs.start_time.slice(0, 5) === time
    );

    if (!blockSchedule) return null;

    // 예외 확인
    const override = blockSchedule.overrides?.find(o => o.date === date);
    
    return {
      blockSchedule,
      override,
      isBlocked: override ? !override.is_open : true
    };
  };

  const lessonStartTimes = getAllTimeSlots();

  const handleConfigSave = () => {
    api.get("/lesson-config")
      .then(res => setLessonConfig(res.data));
    api.get("/lesson-config/overrides")
      .then(res => setLessonOverrides(res.data));
    api.get("/block-schedules")
      .then(res => setBlockSchedules(res.data));
  };

  useEffect(()=>{
    const ws = dayjs(weekStart);
    setWeekDates(Array(6).fill().map((_,i)=>ws.add(i,"day").format("YYYY-MM-DD")));
  }, [weekStart]);

  const isHoliday = (dateStr) => {
    return holidays.some(h => h.date === dateStr);
  };

  const getHolidayInfo = (dateStr) => {
    return holidays.find(h => h.date === dateStr);
  };

  // 주가 바뀌면 예약과 휴일을 함께 받아 한 번에 반영하고, 그 순간 주 전환 모션을 재생한다.
  const { motion: weekMotion, loading: weekLoading } = useWeekTransition(weekStart, (ws) => {
    const endDate = dayjs(ws).add(5, "day").format("YYYY-MM-DD");
    return Promise.all([
      api.get("/lessons", { params: { week_start: ws } }).catch(() => null),
      api.get("/holidays", { params: { start_date: ws, end_date: endDate } }).catch(() => null),
    ]).then(([lessonRes, holidayRes]) => () => {
      if (lessonRes) setReservations(lessonRes.data);
      if (holidayRes) setHolidays(holidayRes.data);
    });
  });

  // 주 이동 — ◀▶ 버튼, 모바일 스와이프(시간표 위), PC 키보드(← → T) 공용
  const moveWeek = (step) => setWeekStart(dayjs(weekStart).add(7 * step, "day").format("YYYY-MM-DD"));
  const weekSwipeHandlers = useHorizontalSwipe((step) => {
    if (navigator.vibrate) navigator.vibrate(30);
    moveWeek(step);
  });
  useCalendarShortcuts({
    onPrev: () => moveWeek(-1),
    onNext: () => moveWeek(1),
    onToday: () => setWeekStart(getCurrentWeekStart()),
  });

  // 보고 있는 주의 지난 요일에도 당시 변동 스케줄이 반영되도록,
  // 주 시작일(과거)과 오늘 중 더 이른 날짜부터 변동 스케줄을 조회한다.
  function overrideFetchStart() {
    const today = dayjs().format("YYYY-MM-DD");
    return weekStart < today ? weekStart : today;
  }

  async function loadTeachersWithOverrides() {
    try {
      const res = await api.get("/teachers");
      const baseTeachers = res.data;

      const teachersWithOverrides = await Promise.all(
        baseTeachers.map(async (t) => {
          try {
            const o = await api.get(`/teachers/${t.id}/schedule-overrides`, {
              params: { start_date: overrideFetchStart() }
            });
            return { ...t, overrides: o.data };
          } catch (err) {
            return { ...t, overrides: [] };
          }
        })
      );

      setTeachers(teachersWithOverrides);

    } catch (err) {
      console.error("teacher load failed:", err);
      setTeachers([]);
    }
  }

  useEffect(()=>{
    loadTeachersWithOverrides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  useEffect(()=>{
    if (isAdminOrTeacher) {
      api.get("/members")
        .then(res => setMembers(res.data))
        .catch(err => {
          console.error("회원 목록 불러오기 실패:", err);
          setMembers([]);
        });
    }
  }, [isAdminOrTeacher]);

  async function refreshMemberTicket(memberId){
    try{
      const res = await api.get(`/members/${memberId}`);
      setReservations(prev => 
        prev.map(r => 
          r.user?.id === memberId 
            ? {...r, user: {...r.user, ticket: res.data.ticket}}
            : r
        )
      );
    }catch(err){
      console.error("refreshMemberTicket 에러:", err);
    }
  }

  function isTeacherAvailableAt(teacher, date, time) {
    const override = teacher.overrides?.find(o => o.date === date);

    if (override) {
      if (override.type === "OFF") return false;

      if (override.type === "ADJUSTED") {
        const start = override.work_start.slice(0, 5);
        const end = override.work_end.slice(0, 5);
        return time >= start && time < end;
      }
    }

    if (!teacher.schedules || teacher.schedules.length === 0) return false;

    const dayOfWeek = dayNames[dayjs(date).day()];

    return teacher.schedules.some(schedule => {
      if (schedule.day !== dayOfWeek) return false;

      const scheduleStart = schedule.start.slice(0, 5);
      const scheduleEnd = schedule.end.slice(0, 5);

      return time >= scheduleStart && time < scheduleEnd;
    });
  }

  function getReservationsForSlot(day, time){
    return reservations.filter(r => r.date===day && r.start_time.slice(0,5)===time);
  }

  function getAvailableTeachers(date, time) {
    return teachers.filter(t => isTeacherAvailableAt(t, date, time));
  }

  function getReservationCountForDate(date) {
    return reservations.filter(r => r.date === date).length;
  }

  function isBlockReservation(reservation) {
    return reservation?.user?.role === 'ADMIN' || reservation?.user?.role === 'TEACHER';
  }

  function handleCellClick(day, time, teacherId, reservation, blockInfo){
    // 블록된 셀 클릭 시 (예약이 없는 경우)
    if (blockInfo?.isBlocked && !reservation) {
      setUnblockDialog({
        open: true,
        day,
        time,
        teacherId,
        blockSchedule: blockInfo.blockSchedule,
        override: blockInfo.override,
        mode: 'date'
      });
      return;
    }

    const actionType = reservation ? "cancel" : "reserve";

    setDialog({
      open: true,
      day,
      time,
      reservation,
      action: actionType,
      teacher_id: teacherId,
      reservationType: 'block-once',
      user_id: reservation?.user?.id || null,
      adjustTicket: true,
      createPractice: true,
    });
  }

  function handleHolidayClick(date) {
    const count = getReservationCountForDate(date);
    setHolidayDialog({
      open: true,
      date,
      refundTickets: true,
      reservationCount: count
    });
  }

  function handleRestoreClick(date) {
    const holiday = getHolidayInfo(date);
    setRestoreDialog({
      open: true,
      date,
      holiday
    });
  }

  // 블록 해제는 두 가지 범위를 지원한다.
  //  - mode 'date'  : 해당 날짜만 예외 처리(BlockScheduleOverride 생성) — 다른 주는 계속 블록
  //  - mode 'fixed' : 고정 블록 행 자체를 삭제 — 해당 요일/시간이 모든 주에서 해제
  // 블록은 30분 단위 행으로 저장되므로(blockScheduleController.create),
  // 'fixed'도 클릭한 칸 하나(요일+시작시각)에만 적용된다.
  // 연속 구간 전체를 지우려면 칸마다 해제하거나 운영 설정 > 블록 스케줄을 쓴다.
  async function handleUnblockConfirm() {
    const { day, time, blockSchedule, mode } = unblockDialog;
    const isFixedMode = mode === 'fixed';

    try {
      if (isFixedMode) {
        // 서버가 연결된 BlockScheduleOverride도 함께 삭제한다.
        await api.delete(`/block-schedules/${blockSchedule.id}`);
        toast(`매주 ${dayKo[blockSchedule.day_of_week] || blockSchedule.day_of_week}요일 ${time} 고정 블록이 해제되었습니다.`);
      } else {
        await api.post("/block-schedules/overrides", {
          block_schedule_id: blockSchedule.id,
          date: day,
          is_open: true,
          reason: "관리자 수동 해제"
        });
        toast("해당 날짜의 블록이 해제되었습니다.");
      }

      // 블록 스케줄 다시 불러오기
      api.get("/block-schedules")
        .then(res => setBlockSchedules(res.data));

      setUnblockDialog({ open: false });
    } catch (err) {
      toast(
        err?.response?.data?.message || (isFixedMode ? "고정 블록 해제 실패" : "블록 해제 실패"),
        "error"
      );
    }
  }

  async function handleHolidayConfirm() {
    try {
      const res = await api.post("/lessons/holiday", {
        date: holidayDialog.date,
        refund_tickets: holidayDialog.refundTickets
      });

      const ticketMsg = holidayDialog.refundTickets 
        ? "티켓이 반환되었습니다." 
        : "티켓은 반환되지 않았습니다.";

      alert({ title: "휴일 지정 완료", message: `${res.data.message}\n삭제된 레슨: ${res.data.deletedCount}건\n${ticketMsg}` });

      setHolidayDialog({ open: false, date: null, refundTickets: true });

      api.get("/lessons", { params: { week_start: weekStart } })
        .then(res => setReservations(res.data));
      
      const startDate = weekDates[0];
      const endDate = weekDates[weekDates.length - 1];
      api.get("/holidays", { params: { start_date: startDate, end_date: endDate } })
        .then(res => setHolidays(res.data));
    } catch (err) {
      toast(err?.response?.data?.message || "휴일 지정 실패", "error");
    }
  }

  async function handleRestoreConfirm() {
    try {
      const res = await api.post("/lessons/restore-workday", {
        date: restoreDialog.date
      });

      let message = `${res.data.message}\n복구된 레슨: ${res.data.restoredCount}건`;
      if (res.data.ticketDeductedUsers?.length > 0) {
        message += `\n티켓 차감된 회원: ${res.data.ticketDeductedUsers.map(u => u.name).join(', ')}`;
      }

      alert({ title: "업무일 복구 완료", message });

      setRestoreDialog({ open: false, date: null, holiday: null });

      api.get("/lessons", { params: { week_start: weekStart } })
        .then(res => setReservations(res.data));
      
      const startDate = weekDates[0];
      const endDate = weekDates[weekDates.length - 1];
      api.get("/holidays", { params: { start_date: startDate, end_date: endDate } })
        .then(res => setHolidays(res.data));
    } catch (err) {
      toast(err?.response?.data?.message || "업무일 복구 실패", "error");
    }
  }

  async function handleDialogAction(){
    const { day, time, reservation, action, teacher_id, reservationType, user_id } = dialog;

    try {
      if(action === "reserve"){
        let targetUserId;
        let isBlock = false;
        
        if (reservationType === 'block-once') {
          targetUserId = user.id;
          isBlock = true;
        } else if (reservationType === 'block-fixed') {
          // 고정 블록 스케줄 생성
          const dayOfWeek = dayNames[dayjs(day).day()];
          
          await api.post("/block-schedules", {
            teacher_id,
            day_of_week: dayOfWeek,
            start_time: time,
            end_time: getEndTime(time),
            reason: "관리자 등록"
          });

          // 블록 스케줄 다시 불러오기
          api.get("/block-schedules")
            .then(res => setBlockSchedules(res.data));

          toast("블록 스케줄(고정)이 등록되었습니다. 매주 해당 요일/시간에 자동으로 적용됩니다.");
          setDialog({ open: false, reservationType: 'block-once' });
          return;
        } else {
          if (!user_id) {
            toast("예약할 회원을 선택해주세요.", "warning");
            return;
          }
          targetUserId = user_id;
          isBlock = false;
        }

        await api.post("/lessons", {
          date: day,
          start_time: time,
          end_time: getEndTime(time),
          teacher_id,
          user_id: targetUserId,
          is_block: isBlock,
          adjust_ticket: dialog.adjustTicket !== false,
          skip_practice: dialog.createPractice === false
        });

      } else if(action === "cancel" && reservation){
        const canceledUserId = reservation.user.id;
        const canceledUserName = reservation.user.name;
        const wasBlockReservation = isBlockReservation(reservation);

        const shouldAdjustTicket = dialog.adjustTicket !== false;
        const shouldRemovePractice = dialog.removePractice !== false;
        await api.delete(`/lessons/${reservation.id}`, {
          params: {
            adjust_ticket: shouldAdjustTicket,
            skip_practice_cleanup: !shouldRemovePractice
          }
        });

        if (!wasBlockReservation) {
          await refreshMemberTicket(canceledUserId);
          if (shouldAdjustTicket) {
            toast(`${canceledUserName}님의 레슨이 취소되어 티켓이 1개 반환되었습니다.`);
          } else {
            toast(`${canceledUserName}님의 레슨이 취소되었습니다. (티켓 변동 없음)`);
          }
        }
      }

      setDialog({ open: false, reservationType: 'block-once' });
      api.get("/lessons", { params: { week_start: weekStart } })
        .then(res => setReservations(res.data));
    } catch(err){
      console.error("handleDialogAction 에러:", err);
      toast(err?.response?.data?.message || "실패", "error");
    }
  }

  // 변동 스케줄 관련 함수 — 관리 다이얼로그에서는 지난 변동도 열람할 수 있게 전체 이력 조회
  async function fetchScheduleOverrides(teacherId) {
    try {
      const res = await api.get(`/teachers/${teacherId}/schedule-overrides`, {
        params: { include_past: true }
      });
      setScheduleOverrides(res.data);
    } catch (err) {
      console.error('변동 스케줄 조회 실패:', err);
      setScheduleOverrides([]);
    }
  }

  // 관리 다이얼로그 목록: 예정(수정/삭제 가능)과 지난 변동(열람 전용) 분리
  const overrideTodayStr = dayjs().format("YYYY-MM-DD");
  const upcomingOverrides = scheduleOverrides.filter(o => o.date >= overrideTodayStr);
  const pastOverrides = scheduleOverrides
    .filter(o => o.date < overrideTodayStr)
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  function resetOverrideForm() {
    setEditingOverrideId(null);
    setOverrideForm({
      date: "",
      type: "OFF",
      work_start: "09:00",
      work_end: "18:00",
      reason: ""
    });
  }

  function handleEditOverrideClick(o) {
    setEditingOverrideId(o.id);
    setOverrideForm({
      date: o.date,
      type: o.type,
      work_start: o.work_start ? o.work_start.slice(0, 5) : "09:00",
      work_end: o.work_end ? o.work_end.slice(0, 5) : "18:00",
      reason: o.reason || ""
    });
  }

  async function handleSubmitOverride() {
    if (!overrideForm.date) {
      toast("날짜를 선택해주세요.", "warning");
      return;
    }

    const isEditing = !!editingOverrideId;
    setLoading(true);
    try {
      let resData;
      if (isEditing) {
        // 날짜는 고정(수정 불가) — 유형/시간/사유만 전송
        const res = await api.put(`/teachers/schedule-overrides/${editingOverrideId}`, {
          type: overrideForm.type,
          work_start: overrideForm.work_start,
          work_end: overrideForm.work_end,
          reason: overrideForm.reason
        });
        resData = res.data;
      } else {
        const res = await api.post(`/teachers/${overrideManageDialog.teacher.id}/schedule-overrides`, overrideForm);
        resData = res.data;
      }

      const migration = resData.migration;
      let alertMsg = "✅ " + resData.message;
      if (migration && migration.migrated > 0) {
        const tc = migration.activeTeacherCount;
        const modeLabel = tc <= 1 ? '같은 방(레슨+연습)' : tc >= 3 ? '분리(레슨 1-3, 연습 4-6)' : '분리(레슨 1-2, 연습 3-6)';
        alertMsg += `\n\n📋 연습실 재배정: ${migration.migrated}건 → ${modeLabel} 모드 (선생님 ${tc}명)`;
        if (migration.manualMoved > 0) {
          alertMsg += `\n🔄 수동 연습실 예약 ${migration.manualMoved}건이 빈 연습실로 이동되었습니다.`;
        }
        if (migration.manualCancelled > 0) {
          alertMsg += `\n⚠️ 수동 연습실 예약 ${migration.manualCancelled}건이 빈 방 없어 취소되었습니다.`;
        }
      }
      alert({ title: isEditing ? "변동 스케줄 수정 완료" : "변동 스케줄 등록 완료", message: alertMsg, severity: "success" });
      resetOverrideForm();
      fetchScheduleOverrides(overrideManageDialog.teacher.id);

      // 선생님 목록 다시 불러오기
      await loadTeachersWithOverrides();
    } catch (e) {
      const errorMessage = e?.response?.data?.message || (isEditing ? '변동 스케줄 수정 실패' : '변동 스케줄 등록 실패');
      alert({ title: `${isEditing ? '수정' : '등록'} 실패`, message: errorMessage, severity: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteOverride(overrideId) {
    if (!(await confirm({ title: "변동 스케줄 삭제", message: "정말 이 변동 스케줄을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;

    try {
      const deleteRes = await api.delete(`/teachers/schedule-overrides/${overrideId}`);
      const delMigration = deleteRes.data.migration;
      let delAlertMsg = "✅ " + deleteRes.data.message;
      if (delMigration && delMigration.migrated > 0) {
        const tc = delMigration.activeTeacherCount;
        const modeLabel = tc <= 1 ? '같은 방(레슨+연습)' : tc >= 3 ? '분리(레슨 1-3, 연습 4-6)' : '분리(레슨 1-2, 연습 3-6)';
        delAlertMsg += `\n\n📋 연습실 재배정: ${delMigration.migrated}건 → ${modeLabel} 모드 (선생님 ${tc}명)`;
        if (delMigration.manualMoved > 0) {
          delAlertMsg += `\n🔄 수동 연습실 예약 ${delMigration.manualMoved}건이 빈 연습실로 이동되었습니다.`;
        }
        if (delMigration.manualCancelled > 0) {
          delAlertMsg += `\n⚠️ 수동 연습실 예약 ${delMigration.manualCancelled}건이 빈 방 없어 취소되었습니다.`;
        }
      }
      alert({ title: "변동 스케줄 삭제 완료", message: delAlertMsg, severity: "success" });
      fetchScheduleOverrides(overrideManageDialog.teacher.id);

      // 선생님 목록 다시 불러오기
      await loadTeachersWithOverrides();
    } catch (e) {
      alert({ title: "삭제 실패", message: "변동 스케줄에 예약된 사용자가 있습니다.", severity: "error" });
    }
  }

  function openOverrideManage(teacher) {
    fetchScheduleOverrides(teacher.id);
    setOverrideManageDialog({ open: true, teacher });
    resetOverrideForm();
  }

  if (!lessonConfig) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ pt: 2, pb: 2, maxWidth: 900, mx: "auto" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: "sticky",
          top: `${TOP_HEADER_HEIGHT}px`,
          zIndex: 3,
          height: WEEK_NAV_HEIGHT,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          mb: 1
        }}
      >
        <Button
          onClick={() => moveWeek(-1)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          ◀
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <WeekJumpPicker
            weekStart={weekStart}
            onChange={setWeekStart}
            label={weekDates.length && `${weekDates[0]} ~ ${weekDates.at(-1)}`}
            labelSx={{ fontWeight: "bold", fontSize: "clamp(0.85rem, 3.8vw, 1rem)" }}
          />
          {isAdmin && (
            <IconButton onClick={() => setConfigDialog(true)} color="primary" size="small">
              <Settings />
            </IconButton>
          )}
        </Box>

        <Button
          onClick={() => moveWeek(1)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          ▶
        </Button>

        {/* 주 데이터 로딩이 길어질 때만 이동 바 하단에 얇은 진행 막대 */}
        {weekLoading && (
          <LinearProgress sx={{ position: "absolute", left: 0, right: 0, bottom: -1, height: 2 }} />
        )}
      </Stack>

      {/* 새 시간표(v2)로 전환 — 선택은 localStorage에 남아 재진입해도 유지된다. */}
      {onSwitchVersion && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, mb: 1 }}>
          <Button
            size="small"
            color="secondary"
            variant="outlined"
            startIcon={<AutoAwesome />}
            onClick={onSwitchVersion}
          >
            새 버전 보기
          </Button>
        </Box>
      )}

      {/* overflowX는 "auto"가 아니라 "clip" — auto면 overflowY까지 auto로 승격되어
          Paper가 스크롤 컨테이너가 되고, 내부 요일 헤더의 sticky가 뷰포트 기준으로 동작하지 않는다. */}
      <Paper
        sx={{
          overflowX: "clip",
          // 주 전환 모션 — 요일 헤더와 각 시간 행(직계 자식)에 같이 건다.
          // 감싸는 Box 없이 직계 자식에 걸어 표 구조(sticky 요일 헤더 포함)를 그대로 둔다.
          ...motionSx(weekMotion, "& > *")
        }}
        {...weekSwipeHandlers}
      >
        <Box sx={{
          display: "grid",
          gridTemplateColumns: TABLE_GRID_COLUMNS,
          bgcolor: isDark ? "grey.900" : "#f7f7f7",
          textAlign: "center",
          position: "sticky",
          top: `${TOP_HEADER_HEIGHT + WEEK_NAV_HEIGHT}px`,
          zIndex: 2,
          boxShadow: isDark ? "0 2px 4px rgba(0,0,0,0.45)" : "0 2px 4px rgba(0,0,0,0.10)"
        }}>
          <Box sx={{
            fontWeight: "bold",
            p: "clamp(0.3rem, 1vw, 0.8rem)",
            borderRight: isDark ? "2px solid #555" : "2px solid #333"
          }} />
          {weekDates.map((d,i)=>{
            const effectiveConfig = getEffectiveConfigForDate(d);
            const hasOverride = effectiveConfig.is_override;
            
            return (
              <Box key={d} sx={{
                fontWeight: "bold",
                py: "clamp(0.3rem, 1vw, 0.8rem)",
                px: "clamp(0.1rem, 0.5vw, 0.8rem)",
                borderRight: isDark ? "2px solid #555" : "2px solid #333",
                fontSize: "clamp(0.6rem, 2vw, 0.9rem)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                bgcolor: isHoliday(d) ? (isDark ? "#1a1a1a" : "#222") : hasOverride ? (isDark ? "#4a3520" : "#fff3e0") : "transparent",
                color: isHoliday(d) ? "#f44336" : "inherit"
              }}>
                <span>{weekDays[i]}<br/>({d.slice(5)})</span>
                {hasOverride && !isHoliday(d) && (
                  <Chip
                    label={effectiveConfig.is_open ? "시간변경" : "휴무"}
                    size="small"
                    color={effectiveConfig.is_open ? "warning" : "error"}
                    sx={{
                      fontSize: "clamp(0.5rem, 1.5vw, 0.6rem)",
                      height: 16,
                      maxWidth: "100%",
                      "& .MuiChip-label": { px: 0.5 }
                    }}
                  />
                )}
                {isAdmin && !isHoliday(d) && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleHolidayClick(d)}
                    sx={{
                      fontSize: "clamp(0.5rem, 1.5vw, 0.65rem)",
                      py: 0,
                      px: 0.25,
                      minWidth: "auto",
                      lineHeight: 1.5,
                      whiteSpace: "nowrap"
                    }}
                  >
                    휴일 지정
                  </Button>
                )}
                {isAdmin && isHoliday(d) && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleRestoreClick(d)}
                    sx={{
                      fontSize: "clamp(0.5rem, 1.5vw, 0.65rem)",
                      py: 0,
                      px: 0.25,
                      minWidth: "auto",
                      lineHeight: 1.5,
                      whiteSpace: "nowrap"
                    }}
                  >
                    휴일 취소
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>

        {lessonStartTimes.map(time => {
          const isValidForAnyDay = weekDates.some(day => isTimeSlotValidForDate(day, time));
          if (!isValidForAnyDay) return null;

          // 정시(00분)인 경우 진한 검정색 줄로 구분
          const isHourStart = time.endsWith(":00");

          return (
            <Box key={time} sx={{
              display: "grid",
              gridTemplateColumns: TABLE_GRID_COLUMNS,
              borderTop: isHourStart ? (isDark ? "1px solid #555" : "1px solid #333") : (isDark ? "1px solid #333" : "1px solid #eee")
            }}>
              <Box sx={{
                p: "clamp(0.2rem, 0.8vw, 0.6rem)",
                bgcolor: isDark ? "grey.900" : "#f7f7f7",
                borderRight: isDark ? "2px solid #555" : "2px solid #333",
                fontSize:"clamp(0.55rem, 1.8vw, 0.8rem)",
                display:"flex",
                flexDirection:"column",
                alignItems:"center",
                justifyContent:"center",
                lineHeight: 1.2
              }}>
                <div>{time}</div>
                <div style={{fontSize: "clamp(0.5rem, 1.5vw, 0.65rem)", color: isDark ? "#999" : "#666"}}>~</div>
                <div>{getEndTime(time)}</div>
              </Box>
              {weekDates.map(day => {
                if (!isTimeSlotValidForDate(day, time)) {
                  return (
                    <Box key={day} sx={{
                      p: "clamp(0.2rem, 0.8vw, 0.5rem)",
                      borderRight: isDark ? "2px solid #555" : "2px solid #333",
                      borderTop: isHourStart ? (isDark ? "1px solid #555" : "1px solid #333") : "none",
                      bgcolor: isDark ? "grey.900" : "#f9f9f9"
                    }} />
                  );
                }

                if (isHoliday(day)) {
                  return (
                    <Box key={day} sx={{
                      p: "clamp(0.2rem, 0.8vw, 0.5rem)",
                      borderRight: isDark ? "2px solid #555" : "2px solid #333",
                      borderTop: isHourStart ? (isDark ? "1px solid #555" : "1px solid #333") : "none",
                      bgcolor: isDark ? "#1a1a1a" : "#222",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <Typography sx={{
                        color: "#f44336",
                        fontWeight: "bold",
                        fontSize: "clamp(0.6rem, 2vw, 0.85rem)"
                      }}>
                        휴일
                      </Typography>
                    </Box>
                  );
                }

                const currentReservations = getReservationsForSlot(day,time);
                const availableTeachers = getAvailableTeachers(day,time);

                return (
                  <Box key={day} sx={{ p: "clamp(0.2rem, 0.8vw, 0.5rem)", borderRight: isDark ? "2px solid #555" : "2px solid #333", borderTop: isHourStart ? (isDark ? "1px solid #555" : "1px solid #333") : "none" }}>
                    {availableTeachers.length === 0 ? (
                      <Box sx={{
                        minHeight: "clamp(25px, 8vw, 35px)",
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"center",
                        bgcolor: isDark ? "grey.800" : "#f5f5f5",
                        color: isDark ? "grey.500" : "#999",
                        fontSize:"clamp(0.55rem, 1.8vw, 0.75rem)",
                        textAlign:"center",
                        lineHeight: 1.2
                      }}>
                        선생님<br/>미출근
                      </Box>
                    ) : (
                      availableTeachers.map(teacher=>{
                        const reservation = currentReservations.find(r=>r.teacher_id===teacher.id);
                        const isFull = !!reservation;
                        const override = getOverrideForSlot(teacher, day);
                        const isBlock = isBlockReservation(reservation);
                        const blockInfo = getBlockScheduleInfo(teacher.id, day, time);
                        const isMyLesson = isTeacher && isFull && !isBlock && reservation && Number(reservation.teacher_id) === Number(user.id);
                        const isMyConfirmed = isMyLesson && !reservation.is_provisional;
                        const isMyProvisional = isMyLesson && !!reservation.is_provisional;

                        let displayContent;
                        if (blockInfo?.isBlocked && !isFull) {
                          displayContent = (
                            <>
                              <span style={{ fontSize: "clamp(0.5rem, 1.5vw, 0.65rem)", whiteSpace: "nowrap", lineHeight: 1.15 }}>블록</span>
                              <span style={{ fontSize: "clamp(0.5rem, 1.5vw, 0.65rem)", whiteSpace: "nowrap", lineHeight: 1.15 }}>(고정)</span>
                            </>
                          );
                        } else if (isFull) {
                          if (isBlock) {
                            displayContent = (
                              <>
                                <b style={{ color: "#fff" }}>블록</b>
                                <span style={{ fontSize: "clamp(0.45rem, 1.5vw, 0.62rem)", whiteSpace: "nowrap", lineHeight: 1.15 }}>({reservation.teacher.name}{" "}t)</span>
                              </>
                            );
                          } else {
                            displayContent = (
                              <>
                                <span>{reservation.user.name}</span>
                                <span style={{ fontSize: "clamp(0.45rem, 1.5vw, 0.62rem)", whiteSpace: "nowrap", lineHeight: 1.15 }}>({reservation.teacher.name} t)</span>
                              </>
                            );
                          }
                        } else {
                          displayContent = `${teacher.name} t`;
                        }

                        let bgColor;
                        if (blockInfo?.isBlocked && !isFull) {
                          bgColor = isDark ? "#4a4a4a" : "#757575";
                        } else if (isFull) {
                          if (isBlock) {
                            bgColor = isDark ? "#2d2d2d" : "#424242";
                          } else if (isMyConfirmed) {
                            bgColor = MY_LESSON_BG;
                          } else {
                            bgColor = reservation.is_provisional ? (isDark ? "#5c5320" : "#fff9c4") : (isDark ? "#b33939" : "#ff6161ff");
                          }
                        } else if (override?.type === "ADJUSTED") {
                          bgColor = isDark ? "#4a2c4a" : "#e1bee7";
                        } else {
                          bgColor = isDark ? "#2d4a2d" : "#c8e6c9";
                        }

                        return (
                          <Box key={teacher.id} sx={{
                            minHeight: "clamp(22px, 7vw, 30px)",
                            mb: "clamp(0.15rem, 0.5vw, 0.3rem)",
                            textAlign:"center",
                            fontSize:"clamp(0.55rem, 1.8vw, 0.75rem)",
                            bgcolor: bgColor,
                            color: (isBlock || blockInfo?.isBlocked || isMyConfirmed) ? "#fff" : (isDark ? "#e8e8e8" : "#222"),
                            fontWeight:500,
                            cursor: "pointer",
                            borderRadius:1,
                            display:"flex",
                            flexDirection:"column",
                            alignItems:"center",
                            justifyContent:"center",
                            wordBreak:"break-word",
                            px: "clamp(0.2rem, 1vw, 0.5rem)",
                            lineHeight: 1.2,
                            outline: isMyProvisional ? `2px dashed ${MY_LESSON_BG}` : "none",
                            outlineOffset: "-2px"
                          }}
                          onClick={()=> handleCellClick(day,time,teacher.id,reservation,blockInfo)}>
                            {displayContent}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Paper>

      <Box sx={{mt:2,p:{ xs: 1.25, sm: 2 },bgcolor: isDark ? "grey.900" : "#f9f9f9",borderRadius:1}}>
        <Box sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          pb: 1, mb: 1.5,
          borderBottom: `1px solid ${isDark ? "#333" : "#e8e8e8"}`
        }}>
          <Typography variant="body2" fontWeight="bold">선생님 근무 시간</Typography>
          {isAdmin && (
            <IconButton
              size="small"
              color="primary"
              onClick={() => setTeacherSelectDialog(true)}
            >
              <Settings />
            </IconButton>
          )}
        </Box>
        <Stack direction="column" gap={1.5} divider={<Divider sx={{ borderColor: isDark ? "#333" : "#e8e8e8" }} />}>
          {teachers.map(teacher => (
            <Box key={teacher.id}>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.75 }}>
                {teacher.name} 선생님
              </Typography>

              <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(auto-fill, minmax(150px, 1fr))" },
                gap: 0.75
              }}>
                {teacher.schedules?.map((s, idx) => (
                  <Box key={`s-${idx}`} sx={{
                    bgcolor: isDark ? "grey.800" : "#fff",
                    border: `1px solid ${isDark ? "#3a3a3a" : "#ececec"}`,
                    borderRadius: 2,
                    px: 1, py: 0.5,
                    display: "flex", alignItems: "baseline", gap: 0.75,
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap"
                  }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>{dayKo[s.day] || s.day}</Box>
                    <Box component="span" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                      {s.start.slice(0,5)}-{s.end.slice(0,5)}
                    </Box>
                  </Box>
                ))}
                {teacher.overrides?.map((o, idx) => (
                  <Box key={`o-${idx}`} sx={{
                    bgcolor: o.type === "OFF"
                      ? (isDark ? "#3e2023" : "#fdecea")
                      : (isDark ? "#1e2f42" : "#e3f2fd"),
                    color: o.type === "OFF"
                      ? (isDark ? "#ef9a9a" : "#c62828")
                      : (isDark ? "#90caf9" : "#1565c0"),
                    borderRadius: 2,
                    px: 1, py: 0.5,
                    display: "flex", alignItems: "baseline", gap: 0.75,
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    opacity: o.date < overrideTodayStr ? 0.55 : 1
                  }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {dayjs(o.date).format("M/D")} {dayKo[dayNames[dayjs(o.date).day()]]}
                    </Box>
                    <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {o.type === "OFF" ? "휴무" : `${o.work_start.slice(0,5)}-${o.work_end.slice(0,5)}`}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
        {teachers.some(t => t.overrides?.length > 0) && (
          <Box sx={{
            display: "flex", gap: 1.5, mt: 1.5, pt: 1,
            borderTop: `1px solid ${isDark ? "#333" : "#e8e8e8"}`,
            fontSize: "0.7rem"
          }}>
            <Box component="span" sx={{ color: isDark ? "#ef9a9a" : "#c62828" }}>● 휴무 변동</Box>
            <Box component="span" sx={{ color: isDark ? "#90caf9" : "#1565c0" }}>● 시간 변동</Box>
          </Box>
        )}
      </Box>

      {/* 예약/취소 다이얼로그 */}
      <Dialog open={dialog.open} onClose={()=>setDialog({ open: false, reservationType: 'block-once' })} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box component="span" sx={{ display: "block", fontWeight: 700 }}>
            {dialog.action === "reserve"
              ? "레슨 예약"
              : isBlockReservation(dialog.reservation)
                ? "블록 예약 취소"
                : "레슨 예약 취소"
            }
          </Box>
          <Box component="span" sx={{ display: "block", fontSize: "0.85rem", fontWeight: 400, color: "text.secondary", mt: 0.25 }}>
            {dialog.day && `${dayjs(dialog.day).format("YYYY년 MM월 DD일")} (${dayKo[dayNames[dayjs(dialog.day).day()]]})`}
            {teachers.find(t=>t.id===dialog.teacher_id)?.name && ` · ${teachers.find(t=>t.id===dialog.teacher_id)?.name} 선생님`}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, px: 2, py: 1.25, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAF7" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
              <Typography variant="body2" color="text.secondary">시간</Typography>
              <Typography variant="body2" fontWeight={600}>{dialog.time} ~ {getEndTime(dialog.time)}</Typography>
            </Box>
            {dialog.action === "cancel" && !isBlockReservation(dialog.reservation) && dialog.reservation?.user?.name && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
                <Typography variant="body2" color="text.secondary">예약자</Typography>
                <Typography variant="body2" fontWeight={600}>{dialog.reservation.user.name}</Typography>
              </Box>
            )}
          </Box>

          {dialog.action === "reserve" && (
            <>
              <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>예약 유형</Typography>
              {[
                { value: 'block-once', title: '블록 예약 (일회성)', desc: '해당 날짜/시간만 차단합니다' },
                { value: 'block-fixed', title: '블록 예약 (고정)', desc: '매주 해당 요일/시간을 자동으로 차단합니다' },
                { value: 'member', title: '회원 대리 예약', desc: '선택한 회원의 레슨을 대신 예약합니다' },
              ].map(opt => {
                const selected = dialog.reservationType === opt.value;
                return (
                  <ButtonBase
                    key={opt.value}
                    onClick={() => setDialog(d => ({ ...d, reservationType: opt.value, user_id: null }))}
                    sx={{
                      display: "flex", width: "100%", textAlign: "left", alignItems: "center", gap: 1.25,
                      border: "1px solid",
                      borderColor: selected ? "primary.main" : "divider",
                      borderRadius: 2.5, px: 1.75, py: 1.1, mb: 1,
                      bgcolor: selected ? (isDark ? "rgba(255,255,255,0.06)" : "#FAFAF7") : "transparent",
                      transition: "border-color 0.15s ease, background-color 0.15s ease",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{opt.title}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{opt.desc}</Typography>
                    </Box>
                    {selected && <Check sx={{ fontSize: 18, color: "secondary.main" }} />}
                  </ButtonBase>
                );
              })}

              {dialog.reservationType === 'member' && (
                <>
                  <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 1 }}>예약할 회원</Typography>
                  <MemberPicker
                    members={members.filter(m => m.is_active !== false)}
                    selectedId={dialog.user_id}
                    onSelect={(id) => setDialog(d => ({ ...d, user_id: id }))}
                    maxHeight={190}
                  />
                  <Box sx={{ mt: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dialog.adjustTicket !== false}
                          onChange={(e) => setDialog(d => ({ ...d, adjustTicket: e.target.checked }))}
                        />
                      }
                      label={<Typography variant="body2">티켓 1개 차감</Typography>}
                      sx={{ display: "flex", mt: 0.5 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dialog.createPractice !== false}
                          onChange={(e) => setDialog(d => ({ ...d, createPractice: e.target.checked }))}
                        />
                      }
                      label={<Typography variant="body2">연습실 자동 생성</Typography>}
                      sx={{ display: "flex", mt: -0.5 }}
                    />
                  </Box>
                </>
              )}
            </>
          )}

          {dialog.action === "cancel" && !isBlockReservation(dialog.reservation) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="error">
                {dialog.reservation?.user?.name}님의 레슨을 취소하시겠습니까?
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dialog.adjustTicket !== false}
                    onChange={(e) => setDialog(d => ({ ...d, adjustTicket: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">티켓 1개 반환</Typography>}
                sx={{ display: "flex", mt: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dialog.removePractice !== false}
                    onChange={(e) => setDialog(d => ({ ...d, removePractice: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">연습실 데이터 함께 제거</Typography>}
                sx={{ display: "flex", mt: -0.5 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={()=>setDialog({ open: false, reservationType: 'block-once' })} variant="outlined" color="inherit" sx={{ flex: 1 }}>
            {dialog.action === "reserve" ? "취소" : "닫기"}
          </Button>
          <Button
            onClick={handleDialogAction}
            color={dialog.action==="cancel"?"error":"primary"}
            variant="contained"
            disabled={dialog.action === "reserve" && dialog.reservationType === 'member' && !dialog.user_id}
            sx={{ flex: 2 }}
          >
            {dialog.action === "reserve"
              ? (dialog.reservationType === 'block-once' ? "블록 예약" :
                 dialog.reservationType === 'block-fixed' ? "블록 스케줄 등록" :
                 dialog.user_id
                   ? `${members.find(m => m.id === dialog.user_id)?.name || "회원"} 대리 예약`
                   : "회원을 선택하세요")
              : "예약 취소"
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* 블록 해제 다이얼로그 — 해제 범위(이 날짜만 / 고정 전체)를 고른다 */}
      <Dialog open={unblockDialog.open} onClose={() => setUnblockDialog({ open: false })} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontWeight: 700 }}>
            <LockOpen color="primary" fontSize="small" />
            블록 해제
          </Box>
          <Box component="span" sx={{ display: "block", fontSize: "0.85rem", fontWeight: 400, color: "text.secondary", mt: 0.25 }}>
            {unblockDialog.day && `${dayjs(unblockDialog.day).format("YYYY년 MM월 DD일")} (${dayKo[dayNames[dayjs(unblockDialog.day).day()]]})`}
            {teachers.find(t => t.id === unblockDialog.teacherId)?.name && ` · ${teachers.find(t => t.id === unblockDialog.teacherId)?.name} 선생님`}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, px: 2, py: 1.25, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAF7" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
              <Typography variant="body2" color="text.secondary">시간</Typography>
              <Typography variant="body2" fontWeight={600}>{unblockDialog.time} ~ {getEndTime(unblockDialog.time)}</Typography>
            </Box>
            {unblockDialog.blockSchedule?.reason && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
                <Typography variant="body2" color="text.secondary">블록 사유</Typography>
                <Typography variant="body2" fontWeight={600}>{unblockDialog.blockSchedule.reason}</Typography>
              </Box>
            )}
          </Box>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>해제 범위</Typography>
          {[
            {
              value: 'date',
              title: '이 날짜만 해제',
              desc: unblockDialog.day
                ? `${dayjs(unblockDialog.day).format("M/D")}만 예약 가능, 다른 주는 계속 블록`
                : '해당 날짜만 예약 가능하게 합니다'
            },
            {
              value: 'fixed',
              title: '고정 블록 해제',
              desc: unblockDialog.day
                ? `매주 ${dayKo[dayNames[dayjs(unblockDialog.day).day()]]}요일 ${unblockDialog.time} 블록을 삭제`
                : '해당 요일/시간의 고정 블록을 삭제합니다'
            },
          ].map(opt => {
            const isFixedOpt = opt.value === 'fixed';
            const selected = (unblockDialog.mode || 'date') === opt.value;
            return (
              <ButtonBase
                key={opt.value}
                onClick={() => setUnblockDialog(d => ({ ...d, mode: opt.value }))}
                sx={{
                  display: "flex", width: "100%", textAlign: "left", alignItems: "center", gap: 1.25,
                  border: "1px solid",
                  borderColor: selected ? (isFixedOpt ? "error.main" : "primary.main") : "divider",
                  borderRadius: 2.5, px: 1.75, py: 1.1, mb: 1,
                  bgcolor: selected ? (isDark ? "rgba(255,255,255,0.06)" : "#FAFAF7") : "transparent",
                  transition: "border-color 0.15s ease, background-color 0.15s ease",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{opt.title}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{opt.desc}</Typography>
                </Box>
                {selected && <Check sx={{ fontSize: 18, color: isFixedOpt ? "error.main" : "secondary.main" }} />}
              </ButtonBase>
            );
          })}

          {unblockDialog.mode === 'fixed' && (
            <Alert severity="warning" sx={{ mt: 0.5, py: 0.25, alignItems: "center" }}>
              <Typography variant="caption" display="block">
                지난 주를 포함한 모든 주에 적용되며, 이 시간대에 등록된 날짜별 예외 설정도 함께 삭제됩니다.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={() => setUnblockDialog({ open: false })} variant="outlined" color="inherit" sx={{ flex: 1 }}>
            취소
          </Button>
          <Button
            onClick={handleUnblockConfirm}
            color={unblockDialog.mode === 'fixed' ? "error" : "primary"}
            variant="contained"
            sx={{ flex: 2 }}
          >
            {unblockDialog.mode === 'fixed' ? "고정 블록 해제" : "이 날짜만 해제"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 휴일 지정 다이얼로그 */}
      <Dialog open={holidayDialog.open} onClose={() => setHolidayDialog({ open: false, date: null, refundTickets: true })}>
        <DialogTitle>휴일 지정</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>{holidayDialog.date}</strong>을(를) 휴일로 지정하시겠습니까?
          </Typography>
          <Typography color="error" sx={{ mb: 2 }}>
            해당 일에 예약된 레슨 {holidayDialog.reservationCount}건이 모두 삭제됩니다.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={holidayDialog.refundTickets}
                onChange={(e) => setHolidayDialog(prev => ({ ...prev, refundTickets: e.target.checked }))}
              />
            }
            label="회원에게 레슨 티켓 반환"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {holidayDialog.refundTickets
              ? "삭제되는 레슨의 회원에게 티켓이 1개씩 반환됩니다."
              : "티켓이 반환되지 않습니다."}
          </Typography>
          <DateImpactPreview date={holidayDialog.date} change={{ kind: "HOLIDAY" }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHolidayDialog({ open: false, date: null, refundTickets: true })}>
            취소
          </Button>
          <Button onClick={handleHolidayConfirm} color="error" variant="contained">
            휴일 지정
          </Button>
        </DialogActions>
      </Dialog>

      {/* 업무일 복구 다이얼로그 */}
      <Dialog open={restoreDialog.open} onClose={() => setRestoreDialog({ open: false, date: null, holiday: null })}>
        <DialogTitle>휴일 지정 취소</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>{restoreDialog.date}</strong> 휴일 지정을 취소하시겠습니까?
          </Typography>
          
          {restoreDialog.holiday?.backup_lessons?.length > 0 ? (
            <>
              <Typography sx={{ mb: 1 }}>
                복구될 레슨: <strong>{restoreDialog.holiday.backup_lessons.length}건</strong>
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'action.hover', p: 1, borderRadius: 1, mb: 2 }}>
                {restoreDialog.holiday.backup_lessons.map((lesson, idx) => (
                  <Typography key={idx} variant="body2">
                    • {lesson.user_name} - {lesson.start_time?.slice(0,5)}
                  </Typography>
                ))}
              </Box>
              {restoreDialog.holiday.refund_tickets && (
                <Typography color="warning.main" variant="body2">
                  ⚠️ 휴일 지정 시 티켓이 반환되었으므로, 복구 시 해당 회원들의 티켓이 다시 차감됩니다.
                </Typography>
              )}
            </>
          ) : (
            <Typography color="text.secondary">
              복구할 레슨이 없습니다.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, date: null, holiday: null })}>
            취소
          </Button>
          <Button onClick={handleRestoreConfirm} color="primary" variant="contained">
            휴일 지정 취소
          </Button>
        </DialogActions>
      </Dialog>

      {/* 선생님 선택 다이얼로그 */}
      <Dialog 
        open={teacherSelectDialog} 
        onClose={() => setTeacherSelectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday />
            변동 스케줄 관리
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            변동 스케줄을 관리할 선생님을 선택하세요.
          </Typography>
          <Stack spacing={1}>
            {teachers.map(teacher => (
              <Button
                key={teacher.id}
                variant="outlined"
                fullWidth
                onClick={() => {
                  setTeacherSelectDialog(false);
                  openOverrideManage(teacher);
                }}
                sx={{ justifyContent: "flex-start", textAlign: "left" }}
              >
                {teacher.name} 선생님
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeacherSelectDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 변동 스케줄 관리 다이얼로그 */}
      <Dialog 
        open={overrideManageDialog.open} 
        onClose={() => { setOverrideManageDialog({ open: false, teacher: null }); resetOverrideForm(); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          변동 스케줄 관리 - {overrideManageDialog.teacher?.name} 선생님
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="info">
              선생님의 휴가 또는 근무 시간 변경시 변동되는 스케줄을 추가합니다.
            </Alert>

            <Box sx={{ p: 2, bgcolor: isDark ? "grey.800" : "#f5f5f5", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                {editingOverrideId ? "변동 스케줄 수정" : "새 변동 스케줄 등록"}
              </Typography>

              <Stack spacing={2}>
                <DateField
                  label="날짜"
                  value={overrideForm.date}
                  onChange={date => setOverrideForm({ ...overrideForm, date })}
                  fullWidth
                  disabled={!!editingOverrideId}
                  helperText={editingOverrideId ? "날짜는 수정할 수 없습니다. 변경하려면 삭제 후 다시 등록하세요." : undefined}
                  disablePast
                />

                <FormControl fullWidth>
                  <InputLabel>변동 유형</InputLabel>
                  <Select
                    label="변동 유형"
                    value={overrideForm.type}
                    onChange={e => setOverrideForm({ ...overrideForm, type: e.target.value })}
                  >
                    <MenuItem value="OFF">휴가</MenuItem>
                    <MenuItem value="ADJUSTED">근무 시간 조정</MenuItem>
                  </Select>
                </FormControl>

                {overrideForm.type === "ADJUSTED" && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>시작 시간</InputLabel>
                      <Select
                        label="시작 시간"
                        value={overrideForm.work_start}
                        onChange={e => setOverrideForm({ ...overrideForm, work_start: e.target.value })}
                      >
                        {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>종료 시간</InputLabel>
                      <Select
                        label="종료 시간"
                        value={overrideForm.work_end}
                        onChange={e => setOverrideForm({ ...overrideForm, work_end: e.target.value })}
                      >
                        {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </>
                )}

                <TextField
                  label="변동 사유 (선택)"
                  value={overrideForm.reason}
                  onChange={e => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />

                {overrideForm.date && (
                  <DateImpactPreview
                    date={overrideForm.date}
                    change={{
                      kind: "TEACHER_OVERRIDE",
                      payload: {
                        teacher_id: overrideManageDialog.teacher?.id,
                        type: overrideForm.type,
                        work_start: overrideForm.work_start,
                        work_end: overrideForm.work_end
                      }
                    }}
                  />
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSubmitOverride}
                    disabled={loading}
                    fullWidth
                  >
                    {editingOverrideId ? "수정 저장" : "등록"}
                  </Button>
                  {editingOverrideId && (
                    <Button
                      variant="outlined"
                      onClick={resetOverrideForm}
                      disabled={loading}
                    >
                      취소
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>등록된 변동 스케줄</Typography>
              
              {scheduleOverrides.length === 0 ? (
                <Typography variant="body2" color="text.secondary">등록된 변동 스케줄이 없습니다.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>날짜</TableCell>
                        <TableCell>유형</TableCell>
                        <TableCell>시간</TableCell>
                        <TableCell>사유</TableCell>
                        <TableCell align="center">관리</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {upcomingOverrides.map(override => (
                        <TableRow key={override.id}>
                          <TableCell>{override.date}</TableCell>
                          <TableCell>
                            <Chip
                              label={override.type === "OFF" ? "휴가" : "시간 조정"}
                              size="small"
                              color={override.type === "OFF" ? "error" : "warning"}
                            />
                          </TableCell>
                          <TableCell>
                            {override.type === "OFF"
                              ? "출근 안 함"
                              : `${override.work_start?.slice(0, 5)} - ${override.work_end?.slice(0, 5)}`
                            }
                          </TableCell>
                          <TableCell>{override.reason || "-"}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditOverrideClick(override)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteOverride(override.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {pastOverrides.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0.5, bgcolor: isDark ? "grey.900" : "grey.50" }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              지난 변동 (열람 전용)
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {pastOverrides.map(override => (
                        <TableRow key={override.id} sx={{ opacity: 0.55 }}>
                          <TableCell>{override.date}</TableCell>
                          <TableCell>
                            <Chip
                              label={override.type === "OFF" ? "휴가" : "시간 조정"}
                              size="small"
                              color={override.type === "OFF" ? "error" : "warning"}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {override.type === "OFF"
                              ? "출근 안 함"
                              : `${override.work_start?.slice(0, 5)} - ${override.work_end?.slice(0, 5)}`
                            }
                          </TableCell>
                          <TableCell>{override.reason || "-"}</TableCell>
                          <TableCell align="center">-</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOverrideManageDialog({ open: false, teacher: null }); resetOverrideForm(); }}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 레슨 설정 다이얼로그 */}
      <LessonConfigDialog
        open={configDialog}
        onClose={() => setConfigDialog(false)}
        onSave={handleConfigSave}
      />
    </Box>
  );
}

export default AdminLessonPage;